import { Check, Flame, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/layout/MobileShell";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Pill } from "@/components/ui/Pill";
import { useObservable } from "@/hooks/useObservable";
import { useProfile } from "@/hooks/useProfile";
import { loggedDateOf } from "@/lib/loggedDate";
import {
  boundsFromWeekRecord,
  currentWeekBounds,
  DEFAULT_WEEK_START_DAY,
  formatDateOnly,
  isLastDayOfWeek,
  viewedWeekLabel,
} from "@/lib/week";
import { familyService } from "@/services/family.service";
import { weekLogService } from "@/services/weekLog.service";
import type { Profile } from "@/types/domain";

// Indexed by JS getDay() (0=Sunday).
const NB_DAYS_SHORT = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"];
const TODAY_DOW = new Date().getDay(); // 0=Sun

export function ChildWeek() {
  const { profile } = useProfile();
  const weekLogState = useObservable(weekLogService.state$, {
    week: null,
    logs: [],
    siblingTotals: {},
    isPreviousWeek: false,
    pendingPreviousWeek: null,
    allWeeks: [],
    allLogs: [],
    loading: true,
  });
  const familyState = useObservable(familyService.state$, {
    members: [],
    settings: null,
    loading: false,
  });
  const navigate = useNavigate();

  if (!profile || weekLogState.loading || familyState.loading) return null;

  const { week, logs, siblingTotals, isPreviousWeek, pendingPreviousWeek } =
    weekLogState;
  const weekTotal = weekLogService.weekTotal();
  const compareMax = Math.max(weekTotal, ...Object.values(siblingTotals), 100);
  const streak = week?.data.streak ?? 0;
  const offset = familyState.settings?.week_start_day ?? DEFAULT_WEEK_START_DAY;
  const canConfirmNow =
    week?.data.status === "open" && (isPreviousWeek || isLastDayOfWeek(offset));

  // Active-week bounds drive the day strip (handles transition weeks).
  const activeBounds = week
    ? boundsFromWeekRecord(week.data)
    : currentWeekBounds(offset);
  const isTransitionWeek = activeBounds.length_days !== 7;
  const byDay = Array.from({ length: activeBounds.length_days }, (_, i) => {
    const day = new Date(activeBounds.start);
    day.setDate(activeBounds.start.getDate() + i);
    const dow = day.getDay();
    const dayLogs = logs.filter((l) => loggedDateOf(l) === formatDateOnly(day));
    const total = dayLogs.reduce((s, l) => s + l.data.amount, 0);
    const isToday = !isPreviousWeek && TODAY_DOW === dow;
    return { label: NB_DAYS_SHORT[dow], total, isToday };
  });

  const siblings = familyState.members.filter(
    (m) => m.data.role === "child" && m.id !== profile.id,
  );

  // Group logs by job, sorted by total income descending
  const jobSummary = Object.values(
    logs.reduce<
      Record<
        string,
        { emoji: string; title: string; count: number; total: number }
      >
    >((acc, l) => {
      const { job_id, job_snapshot, amount } = l.data;
      if (!acc[job_id]) {
        acc[job_id] = {
          emoji: job_snapshot.emoji,
          title: job_snapshot.title,
          count: 0,
          total: 0,
        };
      }
      acc[job_id].count += 1;
      acc[job_id].total += amount;
      return acc;
    }, {}),
  ).sort((a, b) => b.total - a.total);

  return (
    <MobileShell>
      <div style={{ padding: "52px 20px 20px" }}>
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: 0.7,
              fontWeight: 700,
            }}
          >
            Uke {week?.data.iso_week ?? activeBounds.iso_week} ·{" "}
            {week?.data.week_range_label ?? ""}
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 30,
              fontWeight: 400,
              marginTop: 2,
              letterSpacing: -0.6,
            }}
          >
            {isPreviousWeek ? "Forrige uke" : "Uka di"}
          </div>
          {isTransitionWeek && (
            <div style={{ marginTop: 8 }}>
              <Pill tone="butter" data-testid="transition-week-badge">
                Overgangsuke · {activeBounds.length_days} dager
              </Pill>
            </div>
          )}
        </div>

        {isPreviousWeek && week && (
          <div
            data-testid="previous-week-banner"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#F9D6CD",
              border: "1px solid var(--bad)",
              borderRadius: "var(--r-md)",
              padding: "12px 14px",
              marginBottom: 14,
              fontSize: 13,
              color: "var(--bad)",
              fontWeight: 600,
              lineHeight: 1.35,
            }}
          >
            <Lock size={18} color="var(--bad)" />
            <span>
              Forrige uke (uke {week.data.iso_week}) må bekreftes før du kan
              starte denne uka.
            </span>
          </div>
        )}

        {!isPreviousWeek && pendingPreviousWeek && (
          <div style={{ marginBottom: 14 }}>
            <Pill tone="warn" data-testid="pending-previous-week-pill">
              Uke {pendingPreviousWeek.data.iso_week} venter på foreldre
            </Pill>
          </div>
        )}

        {/* Earned + bars */}
        <Card style={{ marginBottom: 12, padding: "22px 22px 20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <div
              data-testid="week-earned-label"
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.8,
                color: "var(--ink-3)",
              }}
            >
              Tjent{" "}
              {viewedWeekLabel(
                isPreviousWeek,
                week?.data.iso_week ?? activeBounds.iso_week,
              )}
            </div>
            {streak > 0 && (
              <Pill tone="butter" size="sm">
                <Flame size={11} color="#E2745A" fill="#E2745A" /> {streak} d
                strek
              </Pill>
            )}
          </div>
          <div
            className="num"
            style={{
              fontSize: 52,
              fontWeight: 700,
              letterSpacing: -2,
              marginTop: 2,
              color: "var(--good)",
            }}
          >
            +{weekTotal}{" "}
            <span style={{ fontSize: 20, color: "var(--ink)" }}>kr</span>
          </div>
          {/* Day bars */}
          <div style={{ display: "flex", gap: 4, marginTop: 16 }}>
            {byDay.map(({ label, total, isToday }) => {
              const maxVal = Math.max(...byDay.map((d) => d.total), 50);
              return (
                <div
                  key={label}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 60,
                      background: "var(--bg-2)",
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "flex-end",
                      overflow: "hidden",
                      border: isToday ? "1.5px dashed var(--good)" : "none",
                    }}
                  >
                    {total > 0 && (
                      <div
                        style={{
                          width: "100%",
                          height: `${(total / maxVal) * 100}%`,
                          background: "var(--good)",
                          borderRadius: 6,
                        }}
                      />
                    )}
                  </div>
                  <div
                    className="num"
                    style={{
                      fontSize: 9,
                      color: "var(--ink-3)",
                      fontWeight: 600,
                    }}
                  >
                    {total || "-"}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--ink-3)",
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Job summary */}
        {jobSummary.length > 0 && (
          <Card
            style={{ marginBottom: 12, padding: "18px 22px" }}
            data-testid="job-summary"
          >
            <div
              data-testid="week-jobs-label"
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.7,
                color: "var(--ink-2)",
                marginBottom: 12,
              }}
            >
              Jobber{" "}
              {viewedWeekLabel(
                isPreviousWeek,
                week?.data.iso_week ?? activeBounds.iso_week,
              )}
            </div>
            {jobSummary.map(({ emoji, title, count, total }, i) => (
              <div
                key={title}
                data-testid="job-summary-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  ...(i < jobSummary.length - 1 && {
                    paddingBottom: 10,
                    marginBottom: 10,
                    borderBottom: "1px solid var(--bg-2)",
                  }),
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 22 }}>{emoji}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
                    <div
                      style={{ fontSize: 12, color: "var(--ink-3)" }}
                      data-testid="job-summary-count"
                    >
                      {count}×
                    </div>
                  </div>
                </div>
                <div
                  className="num"
                  data-testid="job-summary-total"
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "var(--good)",
                  }}
                >
                  +{total} kr
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Sibling comparison */}
        {siblings.length > 0 && (
          <Card style={{ marginBottom: 12 }} data-testid="sibling-comparison">
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.7,
                color: "var(--ink-2)",
                marginBottom: 12,
              }}
            >
              Søsken-konkurranse
            </div>
            <CompareRow
              person={profile}
              amount={weekTotal}
              maxAmount={compareMax}
              highlight
              testid="sibling-row-self"
            />
            {siblings.map((s) => (
              <CompareRow
                key={s.id}
                person={s}
                amount={siblingTotals[s.id] ?? 0}
                maxAmount={compareMax}
                testid="sibling-row"
              />
            ))}
          </Card>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          {week?.data.status === "open" && canConfirmNow && (
            <Button
              variant="primary"
              size="md"
              icon={<Check size={16} color="#fff" />}
              fullWidth
              data-testid="confirm-week-button"
              onClick={() => navigate("/child/confirm")}
            >
              Bekreft uka
            </Button>
          )}
          {week?.data.status === "open" && !canConfirmNow && (
            <Pill tone="neutral" data-testid="confirm-week-locked-pill">
              Bekreft uka på søndag
            </Pill>
          )}
          {week?.data.status === "child_confirmed" && (
            <Pill tone="good" data-testid="week-confirmed-pill">
              Sendt til foreldre ✓
            </Pill>
          )}
        </div>
      </div>
    </MobileShell>
  );
}

function CompareRow({
  person,
  amount,
  maxAmount,
  highlight,
  testid,
}: {
  person: Profile;
  amount: number;
  maxAmount: number;
  highlight?: boolean;
  testid?: string;
}) {
  const pct = maxAmount > 0 ? (amount / maxAmount) * 100 : 0;
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 8,
      }}
    >
      <Avatar person={person} size={28} />
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {person.data.name}
            {highlight && " (deg)"}
          </span>
          <span
            className="num"
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: highlight ? "var(--good)" : "var(--ink-2)",
            }}
          >
            {amount} kr
          </span>
        </div>
        <div
          style={{
            height: 8,
            borderRadius: 4,
            background: "var(--bg-2)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: person.data.color,
            }}
          />
        </div>
      </div>
    </div>
  );
}
